import {CorpusMenuService} from '@modules/corpus/core/services/corpus-menu.service';
import {Platform} from '@angular/cdk/platform';
import {DOCUMENT, NgClass, NgIf} from '@angular/common';
import {Component, Inject, inject, OnDestroy, OnInit} from '@angular/core';
import {ExtendedModule} from '@angular/flex-layout';
import {MatMomentDateModule} from '@angular/material-moment-adapter';
import {MatBottomSheetModule} from '@angular/material/bottom-sheet';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule, MatIconRegistry} from '@angular/material/icon';
import {MatSidenavModule} from '@angular/material/sidenav';
import {HammerModule, Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {AccountManagementProviderService} from '@modules/account-management';
import {AuthenticationService, UserDataEntity} from '@modules/authentication';
import {AuthorizationService} from '@modules/authorization';
import {BrowserTestService} from '@modules/browser-test';
import {CommunicationCenterService} from '@modules/communication-center';
import {LrsModule} from '@modules/lrs';
import {LangChangeEvent, TranslateModule, TranslateService} from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import {Angulartics2Matomo} from 'angulartics2';
import {CompassService} from 'fuse-core/compass/compass.service';
import {FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule} from 'fuse-core/components';
import {MobileRedirectionModalComponent} from 'fuse-core/components/mobile-redirection-modal/mobile-redirection-modal.component';
import {FuseSidebarService} from 'fuse-core/components/sidebar/sidebar.service';
import {NewsModule} from 'fuse-core/news/news.module';
import {GlobalConfigurationService} from 'fuse-core/services/global-configuration.service';
import {LayoutConfigService} from 'fuse-core/services/layout-config.service';
import {FuseSplashScreenService} from 'fuse-core/services/splash-screen.service';
import {FuseTranslationLoaderService} from 'fuse-core/services/translation-loader.service';
import {FuseSharedModule} from 'fuse-core/shared.module';
import {LayoutConfig} from 'fuse-core/types';
import {capitalize} from 'lodash-es';
import {OctopusConnectService} from 'octopus-connect';
import {Subject} from 'rxjs';
import {timer} from 'rxjs/internal/observable/timer';
import {filter, map, mergeMap, takeUntil} from 'rxjs/operators';
import {LanguageService} from 'shared/language.service';
import {SharedTranslateModule} from 'shared/shared-translate.module';
import {isMobile} from 'shared/utils/navigator';
import {IsAllowedBySettings} from '../../../../src/app/guards';
import {HorizontalLayout1Module} from '../../../../src/app/layout/horizontal/layout-1/layout-1.module';
import {LayoutModule} from '../../../../src/app/layout/layout.module';
import {VerticalLayout1Module} from '../../../../src/app/layout/vertical/layout-1/layout-1.module';
import {VerticalLayout2Module} from '../../../../src/app/layout/vertical/layout-2/layout-2.module';
import {VerticalLayout3Module} from '../../../../src/app/layout/vertical/layout-3/layout-3.module';

import {DynamicNavigationService} from '../../../../src/app/navigation/dynamic-navigation.service';
import {
    brand,
    defaultMetaDescription,
    brandTitle,
    matomoTrackingIDFromSettings,
    matomoTracking,
    mobileUrlRedirection,
    isMobileAlert,
    customLayoutConfig,
} from '../../../../src/app/settings/verdinum';
import {TralaTranslationLoaderService} from '../../../../src/app/trala-translation-loader.service';
import {TtsModule} from '../../../../src/app/tts/tts.module';
import {PrettyEasyMenuInitializerService} from './menu/pretty-easy-menu-initializer.service';
import {ServiceInstantiator} from './service-instantiator.class';
import {GroupsManagementInitializerService} from '@modules/groups-management/core/services/groups-management-initializer.service';
import {AssignationInitializerService} from '@modules/assignation/core/services/assignation-initializer.service';
import {AccessibilityService} from 'fuse-core/services/accessibility.service';

@Component({
    selector: 'app-root',
    imports: [
        ExtendedModule,
        FuseProgressBarModule,
        HorizontalLayout1Module,
        NewsModule,
        NgClass,
        NgIf,
        TranslateModule,
        VerticalLayout1Module,
        VerticalLayout2Module,
        VerticalLayout3Module,
        SharedTranslateModule,
        MatMomentDateModule,
        MatExpansionModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatSidenavModule,
        MatDialogModule,
        MatBottomSheetModule,
        FuseSharedModule,
        FuseSidebarModule,
        FuseThemeOptionsModule,
        TtsModule,
        LayoutModule,
        LrsModule,
        HammerModule,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'verdinum';
    lang: string;
    browserLang: string;
    themeColor = 'red';
    layoutConfig: LayoutConfig;
    public isAllowedSkipNavigation = false;
    public brand: string = brand;
    private brandTitle: string = brandTitle;
    private defaultMetaDescription: any = defaultMetaDescription;
    private matomoTrackingID: string = matomoTrackingIDFromSettings;
    private matomoTracking: boolean = matomoTracking;
    private _unsubscribeAll: Subject<void>;
    public globalConfigurationService = inject(GlobalConfigurationService);
    private languageService = inject(LanguageService);
    private route = inject(ActivatedRoute);
    private isRouteAccessible = inject(IsAllowedBySettings);
    private dynamicNavigation = inject(DynamicNavigationService);
    private router = inject(Router);
    private communicationCenter = inject(CommunicationCenterService);
    private authService = inject(AuthenticationService);
    private authorizationService = inject(AuthorizationService);
    private dialog = inject(MatDialog);
    private _layoutConfigService = inject(LayoutConfigService);
    private _fuseSidebarService = inject(FuseSidebarService);
    private _fuseSplashScreenService = inject(FuseSplashScreenService);
    private _fuseTranslationLoaderService = inject(FuseTranslationLoaderService);
    private _translateService = inject(TranslateService);
    private _platform = inject(Platform);
    private translationLoader = inject(TralaTranslationLoaderService);
    private _serviceInstantiator = inject(ServiceInstantiator);
    private browserTestService = inject(BrowserTestService);
    private matIconRegistry = inject(MatIconRegistry);
    private connector = inject(OctopusConnectService);
    private usersProvider = inject(AccountManagementProviderService);
    private titleService = inject(Title);
    private angularticsMatomo = inject(Angulartics2Matomo);
    private meta = inject(Meta);
    private compassService = inject(CompassService);
    private corpusMenuService = inject(CorpusMenuService); // Inject CorpusMenuService to initialize it
    private groupsInitializer = inject(GroupsManagementInitializerService);
    private assignationInitService = inject(AssignationInitializerService);
    private accessibilityService = inject(AccessibilityService);
    private prettyEasyMenuInitializerService = inject(PrettyEasyMenuInitializerService);
    private activatedRoute = inject(ActivatedRoute);

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private document: Document,
    ) {
        this._unsubscribeAll = new Subject<void>();
        this.initNavigation();
        this.initLangManagement();
        this.initMobilePlatformManagement();
        this.initLayoutConfigManagement();
        this.assignationInitService.initialize();
        this.corpusMenuService.initialize();
        this.groupsInitializer.initialize();
        // add default meta description if exists
        if (this.defaultMetaDescription && this.defaultMetaDescription[this._translateService.currentLang]) {
            this.meta.addTag({
                name: 'description',
                content: this.defaultMetaDescription[this._translateService.currentLang]
            });
        }

        this.communicationCenter
            .getRoom('corpus')
            .getSubject('displayEmptyCorpusHelper')
            .next(true);

        this.communicationCenter
            .getRoom('authentication')
            .getSubject<UserDataEntity>('userData')
            .subscribe((user) => {
                    this.initTracking();
                if (user) {
                    this.postAuthentication(user);
                } else {
                    this.postLogout();
                }
            });

        /** CONFIG plateforme Inclusive **/
        this.compassService.isSearchActive = true;
    }

    ngOnInit(): void {
        this.prettyEasyMenuInitializerService.initializeMenu();
        this.initializeBodyClassRouteListening();
        this.accessibilityService.init();
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                // if in whitelist array and is not authenticated
                if (!this.authService.isAuthenticated) {
                    const whiteListUrls = ['login', 'register', 'forgot-password'];
                    for (let i = 0; i < whiteListUrls.length; i++) {
                        if (window.location.href.indexOf(whiteListUrls[i]) > -1) {
                            this.initTracking();
                        }
                    }
                }
            });

        this.document.body.classList.add(this.brand);
        this.document.querySelector<HTMLLinkElement>('link[ rel *= "icon"]').href =
            'assets/' + this.brand + '/favicon.png?v=6';
        this.setTitle(this.brandTitle);
        // Subscribe to config changes
        this._layoutConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                this.layoutConfig = config;

                if (this.layoutConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }
            });

        requestIdleCallback(() => {
            this.browserTestService.checkCompatibility();
        });

        // La vérification initiale de l'authentification et la gestion de l'état
        // (y compris l'erreur 401 initiale) sont maintenant entièrement gérées
        // par AuthenticationService.
        // AppComponent réagit aux changements d'état via son abonnement
        // au 'userData' et 'doLogout' du communicationCenter (voir lignes 167-178).
        // La suppression de ce bloc évite un appel /users/me redondant.
    }

    private initializeBodyClassRouteListening(): void {
        /** Ca permet de changer la classe du body en fonction de la route pour pouvoir faire du styling spécifique à une route, il faut mettre ça ailleurs pour le mutualiser **/
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => route.data ? route.data : null)
            )
            .subscribe((evt) => {
                let menuId: string;

                if (evt && evt['menu']) {
                    menuId = evt['menu'];
                } else {
                    menuId = 'level0';
                }
                this.dynamicNavigation.switchClassPath(menuId);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    public setTitle(newTitle: string): void {
        this.titleService.setTitle(newTitle);
    }


    loadMatomo(trackingID: string): void {
        const matomoScript = document.createElement('script');
        matomoScript.setAttribute('type', `text/javascript`);
        matomoScript.innerText = `var _paq = window._paq || [];_paq.push([\'enableLinkTracking\']);(function() {var u=\'https://matomo.tralalere.com/\';_paq.push([\'setTrackerUrl\', u+\'matomo.php\']);_paq.push([\'setSiteId\', \'${trackingID}\']);var d=document, g=d.createElement(\'script\'), s=d.getElementsByTagName(\'script\')[0];g.type=\'text/javascript\'; g.async=true; g.defer=true; g.src=u+\'matomo.js\'; s.parentNode.insertBefore(g,s);})();`;
        document.documentElement.firstChild.appendChild(matomoScript);
    }

    navigateToAnchor(event: Event) {
        event.preventDefault();
        const anchorId = 'maincontent';
        const element = document.getElementById(anchorId);

        if (element) {
            element.focus();
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }

    private initLayoutConfigManagement(): void {
        if (customLayoutConfig.hasOwnProperty('default')) {
            if (customLayoutConfig.hasOwnProperty(this.authService.accessLevel)) {
                this._layoutConfigService.setConfig(customLayoutConfig[this.authService.accessLevel], {emitEvent: true});
            } else {
                this._layoutConfigService.setConfig(customLayoutConfig['default'], {emitEvent: true});
            }

            this.communicationCenter
                .getRoom('authentication')
                .getSubject('userData')
                .subscribe(userData => {
                    const role = this.authService.accessLevel;
                    if (customLayoutConfig.hasOwnProperty(role)) {
                        this._layoutConfigService.setConfig(customLayoutConfig[role], {emitEvent: true});
                    } else {
                        this._layoutConfigService.setConfig(customLayoutConfig['default'], {emitEvent: true});
                    }
                });
        }
    }


    private initMobilePlatformManagement(): void {
        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }
    }

    private initTracking(): void {
        // Matomo tracking
        if (this.matomoTracking) {
            this.loadMatomo(this.matomoTrackingID);
            this.angularticsMatomo.startTracking();
        }
    }

    private initLangManagement(): void {
        const lang = this.languageService.getActiveLanguage().id;
        this.document.documentElement.lang = lang;
        this.connector.setLanguage(lang);
        // Set the navigation translations
        this.translationLoader.loadTranslations(...this.languageService.availableLanguages.map((lang) => lang.id));

        this._translateService.onLangChange.subscribe(
            (event: LangChangeEvent) => {
                this.connector.setLanguage(event.lang);
                this.document.documentElement.lang = event.lang;
                this._translateService.currentLang = event.lang;
            }
        );
        if (this.globalConfigurationService.getsubscribeToLanguageChange()) {
            this.checkIfQueryParamsForceLanguage();
            this.languageService.onLanguageChange$.subscribe((lang) => {
                // Update dynamic menu with new language
                this.dynamicNavigation.generateMenu(this.dynamicNavigation.currentMenuId, true);
                // check route with access settings for new language
                this.isRouteAccessible.checkRouteAllowedOrRedirect(this.route.snapshot, this.router.routerState.snapshot);
            });
        }
    }

    private initNavigation(): void {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const slugs = event.url.split('/').slice(1);

                const title = slugs
                    .map((slug) => `${capitalize(slug.split('?')[0])}`)
                    .join(' - ');
                this.setTitle(`${this.brandTitle} | ${title}`);
                this.isAllowedSkipNavigation = !(event.url.includes('register') || event.url.includes('login') || event.url.includes('password'));
            });
    }

    private postAuthentication(user: UserDataEntity): void {
        Sentry.setUser({email: user.get('email')});
        timer(1000).subscribe(() => {
            // isMobileAlert is used to show a msg mobile not supported but without redirection
            if (isMobile() && (mobileUrlRedirection || isMobileAlert)) {
                const dialogRef = this.dialog.open(
                    MobileRedirectionModalComponent,
                    {}
                );
                dialogRef.afterClosed().subscribe((result) => {
                    // isMobileAlert => no redirection only alert
                    if (result && !isMobileAlert) {
                        window.location.href = mobileUrlRedirection;
                    }
                });
            }
        });

        if (['trainer', 'learner'].includes(this.authService.accessLevel)) {
            this.prettyEasyMenuInitializerService.setMenu(this.authService.accessLevel as 'trainer' | 'learner');
        } else {
            this.prettyEasyMenuInitializerService.setMenu('guest');
        }
    }

    private postLogout(): void {
        this.prettyEasyMenuInitializerService.setMenu('guest');
    }


    private checkIfQueryParamsForceLanguage(): void {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
            const queryParams = this.router.parseUrl(this.router.url).queryParams;
            const suggestedLanguageId = queryParams && queryParams['SuggestedLanguage'];

            // if there is no our query param, we don't need to do anything
            if (!suggestedLanguageId) {
                return;
            }
            const suggestedLanguage = this.languageService.availableLanguages.find(lang => lang.id === suggestedLanguageId);

            // if the language is already set, we don't need to do anything
            if (this.languageService.getActiveLanguage() === suggestedLanguage) {
                return;
            }
            this.languageService.setActiveLanguage(suggestedLanguage);

            // and now we remove the suggested language from the query params
            this.router.navigate([], {
                queryParams: {...queryParams, SuggestedLanguage: null},
                queryParamsHandling: 'merge'
            });
        });
    }
}